import React, { useCallback, useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { debounce } from "lodash";
import { CircularProgress, Stack, Typography } from "@mui/material";

import { ListItem } from "../List-item/ListItem";
import { ListGroup } from "../List-group/ListGroup";
import { ToggleWithLabel } from "components/forms";
import { useDealIdSelector, useUserActions } from "store";
import { NotificationMenu } from "../Notification-menu/NotificationMenu";
import { INotification, INotificationsGroup } from "store/types";

const listItemHeight = 104;
interface Props {
  notifications: (INotification | INotificationsGroup)[];
  closeList: () => void;
  hasMore: boolean;
  loading: boolean;
  getNext: () => void;
  filterByDeal: boolean;
  setFilterByDeal: (value: boolean) => void;
}

export const List: React.FC<Props> = ({
  notifications,
  closeList,
  hasMore,
  loading,
  getNext,
  filterByDeal,
  setFilterByDeal
}) => {
  const { updateAllNotificationGroupAction } = useUserActions();
  const listHeadRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const [listHeight, setListHeight] = useState<number>(0);
  const dealId: number | undefined = useDealIdSelector();

  const getNextDebounce = debounce(getNext, 300);

  useEffect(() => {
    if (listHeight && hasMore && !loading) {
      if (listItemHeight * notifications.length < listHeight) {
        getNextDebounce();
      }
    }
  }, [notifications.length, listHeight, filterByDeal]);

  useEffect(() => {
    if (listRef.current && listHeadRef.current) {
      const height = listRef.current.offsetHeight - listHeadRef.current.offsetHeight;
      setListHeight(height);
    }
  }, [listRef.current]);

  const idsInRow = useCallback((notifications: Array<INotification | INotificationsGroup>): string => {
    return notifications
      .reduce((acc: number[], notification) => {
        const ids = (notification as INotificationsGroup)?.notifications?.map((item) => item.id) || [
          (notification as INotification).id
        ];
        return [...acc, ...ids];
      }, [])
      .join();
  }, []);

  const markAllAsRead = useCallback(() => {
    updateAllNotificationGroupAction({
      ids: idsInRow(notifications),
      status: "read"
    });
  }, [updateAllNotificationGroupAction]);

  return (
    <Stack
      ref={listRef}
      sx={{
        width: 440,
        overflow: "hidden",
        bgcolor: "white",
        boxShadow: "0px 0px 4px #242323",
        height: "80vh",
        maxHeight: "840px"
      }}
    >
      <Stack ref={listHeadRef} sx={{ alignItems: "stretch" }}>
        <Stack
          direction="row"
          sx={{
            height: "54px",
            alignItems: "center",
            justifyContent: "space-between",
            p: "0 20px",
            color: "#597380",
            borderBottom: "1px solid #c7c7c7"
          }}
        >
          <Typography sx={{ fontSize: "18px" }}>NOTIFICATION CENTER</Typography>
          <NotificationMenu markAllAsRead={markAllAsRead} />
        </Stack>
        <Stack
          sx={{
            alignItems: "flex-end",
            justifyContent: "center",
            height: 40,
            p: "0 20px",
            color: "#597380",
            borderBottom: "1px solid #c7c7c7",
            span: {
              fontSize: "14px",
              color: "rgba(23, 60, 76, 0.7)",
              fontWeight: "normal"
            }
          }}
        >
          <ToggleWithLabel
            value={filterByDeal}
            onChange={setFilterByDeal}
            label="Filter by current deal"
            labelPosition="right"
            disabled={!dealId}
          />
        </Stack>
      </Stack>
      <InfiniteScroll
        style={{ position: "relative" }}
        hasMore={hasMore}
        next={getNextDebounce}
        dataLength={notifications.length}
        height={listHeight + "px"}
        loader={
          <Stack
            sx={{ height: "100%", width: "100%", position: "absolute" }}
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress size={150} />
          </Stack>
        }
      >
        {notifications.map((notificationOrGroup: INotification | INotificationsGroup, index: number) => {
          if (!(notificationOrGroup as INotificationsGroup).notifications) {
            return <ListItem notification={notificationOrGroup as INotification} key={index} closeList={closeList} />;
          } else {
            return (
              <ListGroup
                elementIndex={index}
                notificationsGroup={notificationOrGroup as INotificationsGroup}
                closeList={closeList}
                key={index}
              />
            );
          }
        })}
      </InfiniteScroll>
    </Stack>
  );
};
